import { BooleanExpr, Code, DateInput, Field, GenericTemplatedBlock, SharedKeyAggregate, SharedKeyClickComputation, StructuredObjectExpr, SummaryExpr, ValueExpr } from "../survey";
import { timezoneToPrettyName } from "./expr_to_sql";

export function generateDate(expr: DateInput): number {
  const { year, month, day, hour, minute, timezone } = expr;
  let dateToReturn = Date.UTC(
    year,
    Math.max(month - 1, 0),
    day,
    hour,
    minute
  );

  if (timezone) {
    // use dateToReturn to account for different offsets during different daylight savings times.
    const localDate = new Date(dateToReturn);
    
    // calculate offset between target timezone and UTC - whatever local time is is our reference point.
    const targetTimezoneDate = new Date(localDate.toLocaleString('en-US', { timeZone: timezone }));
    const utcTimezoneDate = new Date(localDate.toLocaleString('en-US', { timeZone: 'UTC' }));
    const offsetMs = utcTimezoneDate.getTime() - targetTimezoneDate.getTime();

    dateToReturn = dateToReturn + offsetMs;
  }

  return dateToReturn;
}

export function CompileExpressionToJS(cond: BooleanExpr | ValueExpr | GenericTemplatedBlock<BooleanExpr[]> | StructuredObjectExpr, 
  context?: { currentUser?: string }
) {
  function generateConditions(expr: BooleanExpr | ValueExpr | GenericTemplatedBlock<BooleanExpr[]> | SummaryExpr | StructuredObjectExpr): string {
    const dateKinds = ['Last Modified Date', 'As Date', 'Shift Date', 'Now'];
    switch (expr.kind) {
      case 'Structured Object':
        return `{ ${expr.fields.map(field => `${field.field}: ${generateConditions(field.value)}`).join(', ')} }`;
      case 'And':
        return '(' + expr.clauses.map(generateConditions).join(' &&\n ') + ')';
      case 'Or':
        return '(' + expr.clauses.map(generateConditions).join(' ||\n ') + ')';
      case 'Not': 
        return '(! ' + generateConditions(expr.clause) + ')';
      case 'Equals':
        return `info.${expr.field} === ${JSON.stringify(expr.value)}`;
      case 'Not Equal':
        return `(info.${expr.field} !== ${JSON.stringify(expr.value)})`;
      case 'Exists':
        return `(!!info.${expr.field})`;
      case 'DoesntExist':
        return `(!info.${expr.field})`;
      case 'Never':
        return 'false';
      case 'Code Boolean Expr':
        return '(' + expr.code + ')';
        // TOOD: Thread edit times into formulas
      case 'After':
      case 'Before':
        const compare = generateDate({kind: 'Date', ...expr.date, ...expr.time });

        return `Date.now() ${expr.kind === 'After' ? '>' : '<'} ${compare}`;
      case 'Last Modified':
        // Compares if field was modified within time window (ago.amount ago.unit)
        return `(() => {
          const fieldValue = info.${expr.field};
          const createdDate = info.${expr.field}_created_at ? new Date(info.${expr.field}_created_at).getTime() : null;
          if (!createdDate) return false;
          const now = Date.now();
          const ${expr.ago.unit} = ${expr.ago.amount} * ${expr.ago.unit === 'days' ? 24 * 60 * 60 * 1000 : expr.ago.unit === 'weeks' ? 7 * 24 * 60 * 60 * 1000 : 30 * 24 * 60 * 60 * 1000};
          const timeWindow = ${expr.ago.unit};
          return (now - createdDate) < timeWindow;
        })()`;
      case 'Last Modified Date':
        return `(info.${expr.field}_created_at ? new Date(info.${expr.field}_created_at) : null)`;
      case 'Field':
        return `info.${expr.field}`;
      case 'Value Contains':
        let toLowerCase = expr.caseInsensitive ? '.toLowerCase()' : '';
        if (expr.mode === 'comma separated list')
          return `((${generateConditions(expr.value)} || '')${toLowerCase}.split(',').includes(${generateConditions(expr.contains)}${toLowerCase}))`;
        return `((${generateConditions(expr.value)} || '')${toLowerCase}.indexOf(${generateConditions(expr.contains)}${toLowerCase}) !== -1)`;
      case 'Value Equals':
        return `(String(${generateConditions(expr.value)} || '') === String(${generateConditions(expr.equals)} || ''))`;
      case 'Value Not Equal':
        return `(String(${generateConditions(expr.value)} || '') !== String(${generateConditions(expr.notEqual)} || ''))`;
      case 'Value Empty':
        return `(!${generateConditions(expr.value)})`;
      case 'Value Not Empty':
        return `(!!${generateConditions(expr.value)})`;
      case 'Value Less Than':
        if (dateKinds.includes(expr.value.kind)) {
          return `(${generateConditions(expr.value)} < ${generateConditions(expr.lessThan)})`;
        }
        return `(parseFloat(${generateConditions(expr.value)}) < parseFloat(${generateConditions(expr.lessThan)}))`;
      case 'Value Greater Than':
        if (dateKinds.includes(expr.value.kind)) {
          return `(${generateConditions(expr.value)} > ${generateConditions(expr.greaterThan)})`;
        }
        return `(parseFloat(${generateConditions(expr.value)}) > parseFloat(${generateConditions(expr.greaterThan)}))`;
      case 'When':
        return `(() => {${(expr.when || [{
          cond: { kind: 'Never' },
          then: expr.otherwise
        }]).map((e, i) => 
          (i == 0 ? '' : 'else ') + `if (${generateConditions(e.cond)}) { return ${generateConditions(e.then)}; }`).join('\n')
        } else { return ${generateConditions(expr.otherwise)} } })()`;
      case 'Transform String':
        let s = generateConditions(expr.value);
        if (expr.transform === 'lowercase') {
          return `${s}.toLowerCase()`;
        } else if (expr.transform === 'uppercase') {
          return `${s}.toUpperCase()`;
        } else if (expr.transform === 'titlecase') {
          return `${s}.split(' ').map((word) => word[0].toUpperCase() + word.slice(1)).join(' ')`;
        } else if (expr.transform === 'trim') {
          return `${s}.trim()`;
        }
        return s;
      case 'Null':
        return 'null';
      case 'StringValue':
        return JSON.stringify(expr.value);
      case 'TimezoneValue':
        return JSON.stringify(expr.value);
      case 'NumericValue':
      case 'BooleanValue':
        return `${expr.value}`;
      case 'Current User':
        return JSON.stringify(context?.currentUser || 'No user set');
      case 'Applicant Luck':
        // TODO: thread luck into formulas
        return 'Luck not available in computations';
      case 'As Date':
        const val = expr.value.kind === 'Date'
          ? `new Date(${generateDate(expr.value)})`
          : generateConditions(expr.value);

        if (expr.format) {
          const dateOpts = (() => {
            switch (expr.format.dateStyle) {
              case 'short': return "month: 'numeric', day: 'numeric', year: 'numeric'";
              case 'medium': return "month: 'short', day: 'numeric', year: 'numeric'";
              case 'long': return "month: 'long', day: 'numeric', year: 'numeric'";
              case 'full': return "weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'";
              default: return "month: 'short', day: 'numeric', year: 'numeric'";
            }
          })();
          const timeOpts = (() => {
            switch (expr.format.timeStyle) {
              case 'short': return "hour: '2-digit', minute: '2-digit', hour12: true";
              case 'long': return "hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true";
              case 'twentyfourhour': return "hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false";
              case 'hidden': return null;
              default: return "hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false";
            }
          })();
          const timeZone = expr.format.timeZone ? `, timeZone: '${expr.format.timeZone}'` : '';
          const formatOpts = timeOpts ? `${dateOpts}, ${timeOpts}${timeZone}` : `${dateOpts}${timeZone}`;
          const tzSuffix = expr.format.timeZone
            ? ` + ' ' + '${timezoneToPrettyName(expr.format.timeZone)}'`
            : '';

          return `(() => {
            const val = ${val};
            if (val == null || val === '') return null;
            const d = val instanceof Date ? val : new Date(val);
            if (isNaN(d.getTime())) return null;
            return new Intl.DateTimeFormat('en-US', { ${formatOpts} }).format(d)${tzSuffix};
          })()`;
        }

        return `new Date(${val})`;
      case 'Shift Date':
        const generateIntervalField = (field: number | Field | undefined) => 
          (!field || typeof field === 'number') ? (field || 0) : (`+${generateConditions(field)}`);

        // Date.setMonth has undesirable behavior when a given day doesn't exist in a future month.
        // For example, if the date is Jan 31 and you add one month, you get Feb 31, which then converts to
        // March 2 or 3, depending on whether it's a leap year. We want to get Feb 28 or 29, so we have
        // to manually cap the day to the last day of the target month.
        // Convert the input value to a Date object to handle string inputs (e.g., from Field expressions)
        const dateValue = generateConditions(expr.value);
        return `((date) => {
                    if(date && !isNaN(date.getTime())) {
                      date.setFullYear(date.getFullYear() + ${generateIntervalField(expr.interval.years)});

                      const targetMonth = date.getMonth() + ${generateIntervalField(expr.interval.months)};
                      const numDaysInTargetMonth = new Date(date.getFullYear(), targetMonth + 1, 0).getDate();
                      const targetDayInTargetMonth = Math.min(date.getDate(), numDaysInTargetMonth);
                      date.setMonth(targetMonth, targetDayInTargetMonth);

                      date.setDate(date.getDate() + ${generateIntervalField(expr.interval.days)});
                      date.setHours(date.getHours() + ${generateIntervalField(expr.interval.hours)});
                      date.setMinutes(date.getMinutes() + ${generateIntervalField(expr.interval.minutes)});
                    }
                    return date;
                })(new Date(${dateValue}))`;
      case 'Now':
        return 'new Date()';
      case 'Templated Block':
        throw new Error("expressed has not been expanded");
      case 'Count':
        return ``;
      case 'Mean':
        return ``;
      case 'Median':
        return ``;
      case 'Sum':
        return ``;
      case 'LinkTo':
        switch (expr.destination.kind) {
          case 'Payments Page':
            return `'/payments/' || info.uid`;
          case 'Subsurvey':
            return `'/a/' || info.uid || ${JSON.stringify('/' + expr.destination.path)}`;
          case 'Dashboard':
            return JSON.stringify(`/d/${expr.destination.path}`);
          case 'ApplicantIdentities':
            return `'/ad/' || info.uid || ${JSON.stringify('/' + expr.destination.path)}`;
          case 'Custom Link': {
            const idx = expr.destination.path.indexOf(':uid:');
            if (idx === -1) return JSON.stringify(expr.destination.path);
            const before = expr.destination.path.slice(0, idx);
            const after = expr.destination.path.slice(idx + ':uid:'.length);
            const jsParts: string[] = [];
            if (before) jsParts.push(JSON.stringify(before));
            jsParts.push('info.uid');
            if (after) jsParts.push(JSON.stringify(after));
            return jsParts.join(' || ');
          }
        }
      case 'Percentage':
        return `( (parseFloat(${generateConditions(expr.numerator)}) / parseFloat(${generateConditions(expr.denominator)})) * 100 )`;
      case 'Round':
        return `( Number(${generateConditions(expr.value)}).toFixed(${expr.places || 0}) )`
      case 'Add':
        if (Array.isArray(expr.secondValue)) {
          return `( parseFloat(${generateConditions(expr.baseValue)}) + ` + expr.secondValue.map(value => `parseFloat(${generateConditions(value)})`).join(' +\n ') + ')';
        } else {
          return `( parseFloat(${generateConditions(expr.baseValue)}) + parseFloat(${generateConditions(expr.secondValue)}) )`;
        }
      case 'Subtract':
        if (Array.isArray(expr.secondValue)) {
          return `( parseFloat(${generateConditions(expr.baseValue)}) - ` + expr.secondValue.map(value => `parseFloat(${generateConditions(value)})`).join(' -\n ') + ')';
        } else {
          return `( parseFloat(${generateConditions(expr.baseValue)}) - parseFloat(${generateConditions(expr.secondValue)}) )`;
        }
      case 'Multiply':
        if (Array.isArray(expr.secondValue)) {
          return `( parseFloat(${generateConditions(expr.baseValue)}) * ` + expr.secondValue.map(value => `parseFloat(${generateConditions(value)})`).join(' *\n ') + ')';
        } else {
          return `( parseFloat(${generateConditions(expr.baseValue)}) * parseFloat(${generateConditions(expr.secondValue)}) )`;
        }
      case 'Divide':
        if (Array.isArray(expr.secondValue)) {
          return `( parseFloat(${generateConditions(expr.baseValue)}) / ` + expr.secondValue.map(value => `parseFloat(${generateConditions(value)})`).join(' /\n ') + ')';
        } else {
          return `( parseFloat(${generateConditions(expr.baseValue)}) / parseFloat(${generateConditions(expr.secondValue)}) )`;
        }                  
      case 'Bucket Date':
        return `((date) => {
                        if (${JSON.stringify(expr.bucket)} === 'week') date.setDate(date.getDate() - date.getDay() + 1);
                        switch (${JSON.stringify(expr.bucket)}) {
                            case 'year':
                                date.setMonth(0);
                            case 'month':
                                date.setDate(1);
                            case 'week':
                            case 'day':
                                date.setHours(0);
                            case 'hour':
                                date.setMinutes(0);
                            case 'minute':
                                date.setSeconds(0);
                                date.setMilliseconds(0);
                                break;
                        }
                        return date;
                    })(${generateConditions(expr.value)})`;
      case 'Sync Status':
        return `"${expr.kind}"`;
      case 'Expand':
        return `JSON.parse(${expr.value})`;
      case 'Concat':
        return expr.value.map(generateConditions).join(' + "' + (expr.delimiter === '' || expr.delimiter ? expr.delimiter : ' ') + '" +');
      case 'Extract':
        const projection = (Array.isArray(expr.path) ? expr.path : [expr.path]).join('?.');
        return `(() => {
          // prevents potential crashes due to using JSON.parse
          try {
            return JSON.parse(${typeof expr.value === 'string' ? ('info.' + expr.value) : generateConditions(expr.value)})?.${projection}
          } catch {
            return null;
          }
        })()`;
      default:
        let _: never = expr;
    }
    return '';
  }

  return generateConditions(cond);
}

const SIBLINGS = '__siblings';

// Parses ISO ("2024-01-15", "2024-01-15T00:00:00Z") or US ("01/15/2024") date-like strings
// to a { isDate: true, raw, v: <ms timestamp> } entry, or { isDate: false, raw: v, v } via
// parseFloat for anything else (v is NaN if unparseable, filtered out by callers below).
// Date-*only* strings (no time component) are anchored via Date.UTC from their captured
// y/m/d rather than Date.parse, because Date.parse treats a bare ISO date as UTC midnight
// but a bare US date as *local* midnight — same footgun `toDay` in admin-panel/filters.ts
// works around, since round-tripping mixed UTC/local instants through toISOString() (e.g.
// for Mean below) can shift the displayed calendar day depending on the runtime's timezone.
// A date-*time* string (has a "T") is left to Date.parse since it carries its own offset/Z.
const PARSE_DATE_OR_NUMBER_FN = `(function(__raw) {
  if (typeof __raw === 'string') {
    const __iso = /^(\\d{4})-(\\d{2})-(\\d{2})(T.*)?$/.exec(__raw);
    if (__iso) return { isDate: true, raw: __raw, v: __iso[4] ? Date.parse(__raw) : Date.UTC(+__iso[1], +__iso[2] - 1, +__iso[3]) };
    const __us = /^(\\d{1,2})\\/(\\d{1,2})\\/(\\d{4})$/.exec(__raw);
    if (__us) return { isDate: true, raw: __raw, v: Date.UTC(+__us[3], +__us[1] - 1, +__us[2]) };
  }
  const __v = parseFloat(__raw);
  return { isDate: false, raw: __v, v: __v };
})`;

/**
 * Shared codegen for Min/Max: parse each value as a date or number, drop unparseable
 * entries, then bail out to `null` if the parseable values are a mix of dates and plain
 * numbers (comparing a millisecond timestamp against a small number is meaningless) —
 * this shouldn't happen in practice, so `null` signals the result is unreliable rather
 * than silently returning a nonsensical comparison. Otherwise reduce with `comparator`,
 * returning the original raw string for dates so formatting/time-of-day is preserved.
 */
function compileMinMax(source: string, field: string, comparator: '<' | '>'): string {
  return `(() => { const __parse = ${PARSE_DATE_OR_NUMBER_FN}; const __entries = ${source}.map((info) => __parse(info[${JSON.stringify(field)}])).filter((__e) => !isNaN(__e.v)); if (!__entries.length) return null; const __allDates = __entries.every((__e) => __e.isDate); const __noneDates = __entries.every((__e) => !__e.isDate); if (!__allDates && !__noneDates) return null; return __entries.reduce((__acc, __e) => (__acc === null || __e.v ${comparator} __acc.v) ? __e : __acc, null).raw; })()`;
}

function compileAggregate(agg: SharedKeyAggregate, groupRef: string, context?: { currentUser?: string }): string {
  const op = agg.operation;
  const innerWhere = 'where' in op ? op.where : undefined;
  const innerWhereJs = innerWhere ? CompileExpressionToJS(innerWhere, context) : null;
  // If the aggregate has its own where, further filter the group; otherwise consume the group directly.
  const source = innerWhereJs ? `${groupRef}.filter((info) => ${innerWhereJs})` : groupRef;

  switch (op.kind) {
    case 'Count':
      return `${source}.length`;
    case 'Sum':
      return `${source}.reduce((__acc, info) => __acc + (parseFloat(info[${JSON.stringify(op.field)}]) || 0), 0)`;
    case 'Min':
      return compileMinMax(source, op.field, '<');
    case 'Max':
      return compileMinMax(source, op.field, '>');
    case 'Mean':
      // Same date-vs-number parsing and mixed-type null fallback as compileMinMax; the
      // average of dates is returned as an ISO string rather than a raw timestamp so it
      // stays a usable date value.
      return `(() => { const __parse = ${PARSE_DATE_OR_NUMBER_FN}; const __entries = ${source}.map((info) => __parse(info[${JSON.stringify(op.field)}])).filter((__e) => !isNaN(__e.v)); if (!__entries.length) return null; const __allDates = __entries.every((__e) => __e.isDate); const __noneDates = __entries.every((__e) => !__e.isDate); if (!__allDates && !__noneDates) return null; const __avg = __entries.reduce((__s, __e) => __s + __e.v, 0) / __entries.length; return __allDates ? new Date(__avg).toISOString() : __avg; })()`;
    case 'Concat':
      return `${source}.map((info) => info[${JSON.stringify(op.field)}]).join(${JSON.stringify(op.separator ?? ',')})`; // Stringify ensures this is escaped properly
    case 'jsArray':
      return `${source}.map((info) => info[${JSON.stringify(op.field)}])`;
    case 'jsObject':
      // An array of objects: [ {...}, {...}, ... ]
      return `${source}.map((info) => ( ${CompileExpressionToJS(op.field, context)} ))`;
    case 'first':
      // TODO: What's the correct way to handle undefined?
      return `${source}[0]?.[${JSON.stringify(op.field)}]`;
  }
}

/**
 * Compile a SharedKeyComputation's `propagateUpdates` into a JavaScript expression
 * that evaluates, in the scope provided by `createSharedKeyComputationFunction`
 * (which binds `shared_apps`), to a `Record<uid, Record<field, value>>` of updates.
 *
 * The `Custom` form returns the user's raw code unchanged. The `Click` form is
 * expanded into an IIFE that iterates each applicant in `shared_apps`, exposes
 * named `aggregates` on `info`, and applies the `perApplicantUpdates`.
 */
export function CompileSharedKeyComputationToJS(
  computation: Code | SharedKeyClickComputation,
  context?: { currentUser?: string }
): string {
  if (typeof computation === 'string') {
    return computation;
  }

  // Materialize each aggregate group's outer-where filter once so all of its aggregates
  // (and groups with no outer where) reuse __siblings directly without re-filtering.
  const groupDecls: string[] = [];
  const aggregateEntries: string[] = [];
  (computation.aggregates || []).forEach((group, i) => {
    let groupRef: string;
    if (group.where) {
      groupRef = `__g${i}`;
      const whereJs = CompileExpressionToJS(group.where, context);
      groupDecls.push(`const ${groupRef} = ${SIBLINGS}.filter((info) => ${whereJs});`);
    } else {
      groupRef = SIBLINGS;
    }
    group.aggregateList.forEach(aggregate  => {
      aggregateEntries.push(`${JSON.stringify(aggregate.name)}: ${compileAggregate(aggregate, groupRef, context)}`);
    });
  });
  const aggregateLiteral = `{ ${aggregateEntries.join(', ')} }`;

  const AGG = '__agg';
  const UPDATES = '__updates';
  const UID = '__uid';

  const compileUpdate = (update: { field: string, when?: BooleanExpr, value: ValueExpr | StructuredObjectExpr }) => {
    const valueJs = CompileExpressionToJS(update.value, context);
    const assign = `${UPDATES}[${UID}][${JSON.stringify(update.field)}] = ${valueJs};`;
    if (update.when) {
      const whenJs = CompileExpressionToJS(update.when, context);
      return `if (${whenJs}) { ${assign} }`;
    }
    return assign;
  };

  // Outer `when` is emitted as a single enclosing `if` so the shared condition is
  // evaluated once per applicant instead of once per update inside the group.
  const updateStatements = computation.perApplicantUpdates.flatMap(group => {
    const inner = group.updateList.map(compileUpdate);
    if (group.when) {
      const outerJs = CompileExpressionToJS(group.when, context);
      return [`if (${outerJs}) {\n      ${inner.join('\n      ')}\n    }`];
    }
    return inner;
  });

  return `(() => {
  const ${SIBLINGS} = Object.values(shared_apps);
  ${groupDecls.join('\n  ')}
  const ${AGG} = ${aggregateLiteral};
  const ${UPDATES} = {};
  for (const ${UID} of Object.keys(shared_apps)) {
    const info = Object.assign({}, shared_apps[${UID}], ${AGG});
    ${UPDATES}[${UID}] = {};
    ${updateStatements.join('\n    ')}
  }
  return ${UPDATES};
})()`;
}
